import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import * as Sentry from '@sentry/react'
import { supabase } from './supabase'
import { getCustomerByUserId, recordSession } from './profile'

const AuthContext = createContext(null)

function triggerWelcomeMail(session) {
  if (!import.meta.env.PROD || !supabase || !session?.user) return
  if (session.user.user_metadata?.welcome_sent) return
  supabase.functions
    .invoke('welcome-email')
    .catch((err) => console.warn('welcome-email skipped:', err?.message || err))
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [customer, setCustomer] = useState(null)
  const [initializing, setInitializing] = useState(true)
  const [phoneVerified, setPhoneVerified] = useState(false)

  const refreshCustomer = useCallback(async (uid) => {
    if (!supabase || !uid) {
      setCustomer(null)
      return null
    }
    const cust = await getCustomerByUserId(uid)
    setCustomer(cust)
    setPhoneVerified(Boolean(cust?.phone_verified))
    return cust
  }, [])

  useEffect(() => {
    if (!supabase) {
      setInitializing(false)
      return
    }
    let active = true
    supabase.auth.getUser().then(async ({ data: { user } }) => {
      if (!active) return
      setUser(user)
      if (user) await refreshCustomer(user.id)
      setInitializing(false)
    })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (!active) return
      triggerWelcomeMail(session)
      if (import.meta.env.PROD) {
        Sentry.setUser(
          session?.user?.id ? { id: session.user.id, email: session.user.email } : null
        )
        Sentry.addBreadcrumb({
          category: 'auth',
          level: _event === 'SIGNED_OUT' || _event === 'TOKEN_REFRESH_FAILED' ? 'warning' : 'info',
          message: _event,
        })
      }
      setUser(session?.user ?? null)
      if (session?.user) {
        await refreshCustomer(session.user.id)
        recordSession(session.user.id)
      } else {
        setCustomer(null)
        setPhoneVerified(false)
      }
    })

    return () => {
      active = false
      subscription.unsubscribe()
    }
  }, [refreshCustomer])

  const refresh = useCallback(async () => {
    if (!supabase || !user) return
    const { data } = await supabase.auth.getUser()
    if (data?.user) setUser(data.user)
    await refreshCustomer(user.id)
  }, [user, refreshCustomer])

  const value = {
    user,
    customer,
    initializing,
    phoneVerified,
    refresh,
    refreshCustomer,
    requireProfile: refresh,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider')
  return ctx
}
